import { Typography } from 'antd';

import produce from 'immer';

import isEmpty from 'lodash/isEmpty';

import {
  CUSTOM_MISSING_TOOLTIP_FIELDS,
  HEADER_FIELDS,
  HIDDEN_TOOLTIP_FIELDS,
} from 'pages/help-content-management/constants/fields';

const MEASUREMENT_PRECISION =
  'qaSpecification.factPanels.nutritionalInformation.nutritionDetails.measurementPrecision';

const MEASUREMENT_PRECISION_DISPLAY_NAME = 'Quantity Precision';

export const generatePropertiesToNodesTree = (properties) => {
  if (isEmpty(properties)) return [];

  let tree = [];
  let headerFieldNodes = [];

  if (properties?.length > 0) {
    properties.forEach((property) => {
      if (HIDDEN_TOOLTIP_FIELDS.includes(property.fieldName)) {
        return;
      }

      if (
        property?.fieldName?.toLowerCase() ===
        MEASUREMENT_PRECISION?.toLowerCase()
      ) {
        property = {
          ...property,
          displayName: MEASUREMENT_PRECISION_DISPLAY_NAME,
        };
      }

      const isNestedProperty = property?.childFields?.length > 0;
      const node = getNodeTreeData(property, isNestedProperty);

      if (isNestedProperty) {
        tree.push({
          ...node,
          children: generatePropertiesToNodesTree(property.childFields),
        });
      } else {
        if (HEADER_FIELDS.includes(property.fieldName)) {
          headerFieldNodes.push(node);
        } else {
          tree.push(node);
        }
      }
    });
  }

  if (headerFieldNodes.length > 0) {
    let headerGroupNode = getNodeTreeData(
      { displayName: 'Header', fieldName: 'qaSpecification.header' },
      true
    );

    headerGroupNode.children = headerFieldNodes;
    tree.unshift(headerGroupNode);
  }

  return tree;
};

const getNodeTreeData = (property, isNestedProperty) => {
  return {
    title: isNestedProperty ? (
      <Typography.Title
        level={5}
        style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.65)', marginBottom: 0 }}
      >
        {property.displayName}
      </Typography.Title>
    ) : (
      <Typography.Text strong>{property.displayName}</Typography.Text>
    ),
    isLeaf: isNestedProperty ? false : true,
    data: property,
    value: property.fieldName,
    selectable: isNestedProperty ? false : true,
    key: property.fieldName ?? property.displayName,
  };
};

export const flattenFields = (fields, result = {}) => {
  // Handle if input is an array of fields
  if (Array.isArray(fields)) {
    fields.forEach((field) => {
      flattenFields(field, result);
    });
    return result;
  }

  // Process single field object
  if (fields && fields.fieldName) {
    // Add the current field to the result object
    result[fields.fieldName] = {
      displayName: fields.displayName,
      dataType: fields.dataType,
      fieldName: fields.fieldName,
    };

    // Recursively process child fields if they exist
    if (fields.childFields && fields.childFields.length > 0) {
      flattenFields(fields.childFields, result);
    }
  }

  return result;
};

export const getNewRowAfterChangingRow = ({
  data,
  key,
  changedField,
  changedValue,
}) => {
  const index = data.findIndex((item) => item.key === key);

  if (index > -1) {
    return produce(data, (draftFile) => {
      draftFile[index][`${changedField}`] = changedValue;
    });
  }

  return data;
};

/**
 * Main function to flatten a hierarchical object structure with contextual parent names.
 *
 * @param {Array|Object} data - The data to flatten (array or single object)
 * @param {Object} result - The accumulator object for the flattened result (optional)
 * @param {Array} headerFields - Array of field names that should have "Header" as parent (optional)
 * @param {boolean} useDetailedParentNames - Whether to include full path context in parentDisplayName
 * @returns {Object} - Flattened object with contextual parentDisplayName
 */
export const flattenObjectHierarchy = (
  data,
  result = {},
  headerFields = HEADER_FIELDS,
  useDetailedParentNames = true
) => {
  if (isEmpty(data)) {
    return result;
  }

  // Handle both array and single object inputs
  const items = Array.isArray(data) ? data : [data];

  // Process each item in the array
  items.forEach((item) => {
    // Process this individual object
    flattenSingleObject(
      item,
      result,
      null,
      headerFields,
      useDetailedParentNames
    );
  });

  return result;
};

/**
 * Helper function to flatten a single object and its children
 *
 * @param {Object} obj - The object to flatten
 * @param {Object} result - The accumulator object for results
 * @param {string|null} rootDisplayName - The top-level displayName to use for all children
 * @param {Array} headerFields - Array of field names that should have "Header" as parent
 * @param {boolean} useDetailedParentNames - Whether to include full path context in parentDisplayName
 * @param {Array} parentPath - The display name path of parent objects (for contextual naming)
 * @returns {Object} - The updated result object
 */
export const flattenSingleObject = (
  obj,
  result = {},
  rootDisplayName = null,
  headerFields = [],
  useDetailedParentNames = true,
  parentPath = []
) => {
  // If this is the root object, store its displayName
  if (rootDisplayName === null && obj.displayName) {
    rootDisplayName = obj.displayName;

    // Initialize parent path with root name for top-level objects
    if (useDetailedParentNames && parentPath.length === 0) {
      parentPath = [rootDisplayName];
    }
  }

  // If the object has a fieldName and displayName
  if (obj.fieldName && obj.displayName) {
    // Determine if this is a top-level object by checking field structure
    // Top-level fields are expected to have exactly two parts (e.g., 'qaSpecification.allergens')
    const isTopLevel = obj.fieldName.split('.').length === 2;

    // Create a new path for this object's children
    const newParentPath = [...parentPath];

    // If not top-level and not already in path, add to path
    if (!isTopLevel && !parentPath.includes(obj.displayName)) {
      newParentPath.push(obj.displayName);
    }

    // Determine the parent display name
    let parentDisplayName;

    // Check if the field is in the headerFields array
    if (headerFields.includes(obj.fieldName)) {
      parentDisplayName = 'Header';
    } else if (isTopLevel) {
      // For top-level objects, parentDisplayName is null
      parentDisplayName = null;
    } else {
      // For child objects, use detailed parent path if requested
      if (useDetailedParentNames && parentPath.length > 0) {
        // Create a more detailed parent name using the path
        // Format: "Contacts - Primary Business Contact" for fields under that path
        parentDisplayName = parentPath.join(' - ');
      } else {
        // Use the simple root display name otherwise
        parentDisplayName = rootDisplayName;
      }
    }

    // Create a new entry in our result object
    result[obj.fieldName] = {
      displayName: obj.displayName,
      parentDisplayName: parentDisplayName,
      fieldName: obj.fieldName,
    };

    if (
      obj?.fieldName?.toLowerCase() === MEASUREMENT_PRECISION?.toLowerCase()
    ) {
      result[obj.fieldName] = {
        ...result[obj.fieldName],
        displayName: MEASUREMENT_PRECISION_DISPLAY_NAME,
      };
    }

    if (obj.childFields && Array.isArray(obj.childFields)) {
      // Process each child, passing down parent info
      obj.childFields.forEach((childField) => {
        flattenSingleObject(
          childField,
          result,
          rootDisplayName,
          headerFields,
          useDetailedParentNames,
          newParentPath
        );
      });
    }
  }

  return result;
};

export const flattenTooltipForFields = (qaSpecTooltips = []) => {
  if (isEmpty(qaSpecTooltips)) return {};

  let result = {};

  qaSpecTooltips.forEach((item) => {
    const { fieldPath } = item;

    result[fieldPath] = {
      ...item,
    };
  });

  return result;
};

const initializeChildFields = (field) => {
  if (field.childFields === null) {
    field.childFields = [];
  }
  return field.childFields;
};

const addDirectChildFields = (fieldChildFields, missingChildFields) => {
  if (Array.isArray(missingChildFields) && missingChildFields.length > 0) {
    missingChildFields.forEach((missingChildField) => {
      fieldChildFields.push(missingChildField);
    });
  }
};

const addNestedChildFields = (field, missingConfig) => {
  Object.keys(missingConfig).forEach((nestedKey) => {
    if (nestedKey !== 'childFields') {
      const childFieldIndex = field.childFields.findIndex(
        (childField) => childField.fieldName === nestedKey
      );

      if (childFieldIndex !== -1) {
        const childField = field.childFields[childFieldIndex];
        const childFields = initializeChildFields(childField);

        // If it has childFields property, add them directly
        if (missingConfig[nestedKey].childFields) {
          addDirectChildFields(
            childFields,
            missingConfig[nestedKey].childFields
          );
        }
        // Otherwise, it might be another level of nesting
        else if (typeof missingConfig[nestedKey] === 'object') {
          addNestedChildFields(childField, missingConfig[nestedKey]);
        }
      }
    }
  });
};

const processField = (field, missingModuleNames) => {
  if (missingModuleNames.includes(field.fieldName)) {
    const missingConfig = CUSTOM_MISSING_TOOLTIP_FIELDS[field.fieldName];
    const fieldChildFields = initializeChildFields(field);

    if (Array.isArray(missingConfig.childFields)) {
      addDirectChildFields(fieldChildFields, missingConfig.childFields);
    } else if (typeof missingConfig === 'object') {
      addNestedChildFields(field, missingConfig);
    }
  }
};

export const addMissingTooltipFields = (fields = []) => {
  return produce(fields, (draft) => {
    const missingModuleNames = Object.keys(CUSTOM_MISSING_TOOLTIP_FIELDS);

    for (let i = 0; i < draft.length; i++) {
      processField(draft[i], missingModuleNames);
    }
  });
};

export const groupHelpContentProperties = (properties) => {
  if (isEmpty(properties)) return [];

  let result = [];
  let headerFieldNodes = [];

  if (properties?.length > 0) {
    properties.forEach((property) => {
      if (HIDDEN_TOOLTIP_FIELDS.includes(property.fieldName)) {
        return;
      }

      if (
        property?.fieldName?.toLowerCase() ===
        MEASUREMENT_PRECISION?.toLowerCase()
      ) {
        property = {
          ...property,
          displayName: MEASUREMENT_PRECISION_DISPLAY_NAME,
        };
      }

      const isNestedProperty = property?.childFields?.length > 0;

      if (isNestedProperty) {
        result.push({
          ...property,
          children: groupHelpContentProperties(property.childFields),
        });
      } else {
        if (HEADER_FIELDS.includes(property.fieldName)) {
          headerFieldNodes.push(property);
        } else {
          result.push(property);
        }
      }
    });
  }

  if (headerFieldNodes.length > 0) {
    let headerGroupNode = {
      displayName: 'Header',
      fieldName: 'qaSpecification.header',
    };

    headerGroupNode.children = headerFieldNodes;
    result.unshift(headerGroupNode);
  }

  return result;
};

export const getNewTooltipsAfterEditing = (tooltips, changedFieldPath) => {
  let result = [];

  if (tooltips?.length > 0 && changedFieldPath) {
    tooltips.forEach((tooltip) => {
      if (
        tooltip?.fieldPath?.toLowerCase() === changedFieldPath?.toLowerCase()
      ) {
        const newTooltip = {
          ...tooltip,
          status: 'Inactive',
        };
        result.push(newTooltip);
      } else {
        result.push(tooltip);
      }
    });
  }

  return result;
};

const filterRecursively = (items, normalizedSearchValue) => {
  if (!items || !items.length) return [];

  const result = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    const matchesSearch =
      item?.fieldName?.toLowerCase() === normalizedSearchValue;

    const filteredFields = filterRecursively(
      item?.children,
      normalizedSearchValue
    );

    const hasMatchingChildren = filteredFields.length > 0;

    if (matchesSearch) {
      result.push(item);
    } else if (hasMatchingChildren) {
      result.push({
        ...item,
        children: filteredFields,
      });
    }
  }

  return result;
};

export const filterGroupFields = (fields, searchValue) => {
  if (!searchValue) return fields;

  const normalizedSearchValue = searchValue.toLowerCase();

  return filterRecursively(fields, normalizedSearchValue);
};
